// frontend/src/modules/expenses/pages/ReimbursePage.tsx
import * as React from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Wallet, ArrowRight } from 'lucide-react';
import { PageHeader } from '../../../design-system/components/PageHeader';
import { ErrorState } from '../../../design-system/components/ErrorState';
import { NoAccessState } from '../../../design-system/components/NoAccessState';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';
import { useApi } from '../../../design-system/useApi';
import { useBranding } from '../../../core/branding/BrandingProvider';
import { formatDate, formatMoney } from '../../../design-system/format';
import { GlassCard, Eyebrow } from '../../../design-system/pds';
import { expensesApi } from '../api';
import type { Expense } from '../types';
import type { EmployeeWithUser } from '../../hr/types';
import type { PayrollRun } from '../../payroll/types';

/**
 * Push approved + reimbursable expenses for one employee into a chosen DRAFT
 * payroll run as allowance components. The list is filtered to that employee's
 * approved, reimbursable, not-yet-reimbursed expenses.
 */
export function ReimbursePage() {
  const { tenant } = useBranding();
  const tenantCurrency = (tenant as any)?.currency ?? 'USD';

  const employeesQ = useApi<{ employees: EmployeeWithUser[] }>('/api/hr/employees');
  const runsQ = useApi<{ runs: PayrollRun[] }>('/api/payroll/runs');
  const employees = employeesQ.data?.employees ?? [];
  const draftRuns = (runsQ.data?.runs ?? []).filter((r) => r.status === 'draft');

  const [employeeId, setEmployeeId] = React.useState<string>('');
  const [runId, setRunId] = React.useState<string>('');
  const [selected, setSelected] = React.useState<Set<string>>(new Set());
  const [busy, setBusy] = React.useState(false);

  // Approved + reimbursable expenses for the chosen employee.
  const expensesPath = employeeId
    ? `/api/expenses?status=approved&employee_id=${encodeURIComponent(employeeId)}&limit=200`
    : null;
  const { data: expData, loading, error: expError, reload } = useApi<{ rows: Expense[] }>(expensesPath);
  const candidates = (expData?.rows ?? []).filter((e) => e.reimbursable && e.payroll_component_id == null);

  React.useEffect(() => { setSelected(new Set()); }, [employeeId]);

  function toggle(id: string) {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  const total = candidates
    .filter((e) => selected.has(e.id))
    .reduce((sum, e) => sum + Number(e.amount || 0), 0);

  async function submit() {
    if (!runId) { toast.error('Pick a draft payroll run'); return; }
    if (selected.size === 0) { toast.error('Select at least one expense'); return; }
    setBusy(true);
    try {
      const result = await expensesApi.reimburse({
        expense_ids: Array.from(selected),
        run_id: runId,
        employee_id: employeeId,
      });
      if (result.reimbursed.length > 0) {
        toast.success(`Pushed ${result.reimbursed.length} expense${result.reimbursed.length === 1 ? '' : 's'} to payroll`);
      }
      if (result.skipped.length > 0) {
        toast.warning(`${result.skipped.length} skipped`, {
          description: result.skipped.map((s) => `${s.expense_id.slice(0, 8)}: ${s.reason}`).join(', '),
        });
      }
      setSelected(new Set());
      reload();
    } catch (err: any) {
      toast.error('Reimbursement failed', { description: err.message });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen">
      <PageHeader
        eyebrow={
          <>
            <Link to="/dashboard/expenses" className="hover:text-foreground transition-colors">Expenses</Link>
            {' / Reimburse via payroll'}
          </>
        }
        title="Reimburse via payroll"
        description="Bundle an employee's approved, reimbursable expenses into a draft payroll run as allowance line items."
      />

      <div className="px-6 sm:px-8 lg:px-10 pb-12 max-w-3xl space-y-6">
        {/* This page needs HR (employees) + payroll (draft runs) reads — both
            manager-level. Surface a 403 honestly instead of empty pickers. */}
        {(employeesQ.status === 403 || runsQ.status === 403) ? (
          <GlassCard padding="p-0" className="overflow-hidden">
            <NoAccessState description="Reimbursing via payroll needs HR and payroll access. Ask a workspace owner if you think you should have it." />
          </GlassCard>
        ) : (employeesQ.error || runsQ.error) ? (
          <GlassCard padding="p-0" className="overflow-hidden">
            <ErrorState
              title="Couldn't load employees or payroll runs"
              description={employeesQ.error ?? runsQ.error ?? undefined}
              onRetry={() => { employeesQ.reload(); runsQ.reload(); }}
            />
          </GlassCard>
        ) : (
        <>
        <GlassCard padding="p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Employee</Label>
              <Select value={employeeId || undefined} onValueChange={setEmployeeId}>
                <SelectTrigger><SelectValue placeholder="Pick an employee…" /></SelectTrigger>
                <SelectContent>
                  {employees.map((e) => (
                    <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Draft payroll run</Label>
              <Select value={runId || undefined} onValueChange={setRunId}>
                <SelectTrigger><SelectValue placeholder="Pick a draft run…" /></SelectTrigger>
                <SelectContent>
                  {draftRuns.length === 0 ? (
                    <SelectItem value="__none__" disabled>No draft runs — create one in Payroll</SelectItem>
                  ) : (
                    draftRuns.map((r) => (
                      <SelectItem key={r.id} value={r.id}>
                        {formatDate(r.period_start)} – {formatDate(r.period_end)}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>
        </GlassCard>

        {employeeId && (
          <GlassCard padding="p-0" className="overflow-hidden">
            {loading ? (
              <div className="py-14 text-center text-[13px] text-ink/50 dark:text-white/50">Loading…</div>
            ) : expError ? (
              <ErrorState title="Couldn't load this employee's expenses" description={expError} onRetry={reload} />
            ) : candidates.length === 0 ? (
              <div className="py-14 px-8 text-center">
                <Eyebrow className="mb-2">Nothing to reimburse</Eyebrow>
                <p className="text-[13px] text-ink/55 dark:text-white/55">
                  This employee has no approved, reimbursable expenses awaiting payout.
                </p>
              </div>
            ) : (
              <ul className="divide-y divide-ink/6 dark:divide-white/8">
                {candidates.map((e) => (
                  <li key={e.id} className="flex items-center gap-3 px-5 py-3.5">
                    <input
                      type="checkbox"
                      checked={selected.has(e.id)}
                      onChange={() => toggle(e.id)}
                      className="h-4 w-4 rounded accent-coral-600"
                      aria-label={`Select ${e.category_name}`}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] font-semibold text-ink dark:text-white truncate">{e.category_name}</p>
                      <p className="text-[12px] text-ink/55 dark:text-white/55">{formatDate(e.spent_at)}</p>
                    </div>
                    <span className="text-[14px] font-semibold tabular-nums font-mono text-ink dark:text-white">
                      {formatMoney(e.amount, e.currency || tenantCurrency)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </GlassCard>
        )}

        {candidates.length > 0 && (
          <div className="flex items-center justify-between">
            <span className="text-[13px] text-ink/65 dark:text-white/65">
              {selected.size} selected · <strong className="text-ink dark:text-white">{formatMoney(total, tenantCurrency)}</strong>
            </span>
            <Button onClick={submit} disabled={busy || selected.size === 0 || !runId} className="gap-1.5">
              <Wallet className="h-3.5 w-3.5" />
              {busy ? 'Pushing…' : 'Push to payroll'}
              <ArrowRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}
        </>
        )}
      </div>
    </div>
  );
}
